import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { ChevronDown, ChevronRight, Download, Loader2, SlidersHorizontal } from "lucide-react";

import { Button } from "@app/components/ui/button";
import { Checkbox } from "@app/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@app/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import { cn } from "@app/lib/utils";
import type {
  CorrectionMethod,
  StudyVariable,
  TableAnalysisRequest,
  TableResult,
  TableRow,
  TestMode,
} from "@app/types/study";

import { formatInterval, formatMeasure, formatNumber, formatP, formatPercent } from "./format";
import { groupColor } from "@app/lib/palette";
import { MethodsBox, WarningsList } from "./StudyNotes";
import { TestName } from "./TestLibrary";
import { VariableChips, VariablePicker } from "./VariablePicker";

const COUNTABLE = new Set(["categorical", "binary", "multi", "ordinal"]);
// Rank tests read the median; the others read the mean. The summary a row's test reads is the one
// set in weight — a delay of 27 [0 – 155] days read as « 202 ± 449 » is why both are shown.
const RANK_TESTS = new Set(["mann_whitney", "kruskal"]);
// Differences listed before « show the others »: enough to read at a glance, short enough to read.
const HEADLINES = 6;

interface Props {
  variables: StudyVariable[];
  settings: TableAnalysisRequest;
  onSettings: (next: TableAnalysisRequest) => void;
  result: TableResult | null;
  loading: boolean;
  error: string | null;
  onExportCsv: () => void;
  exporting: boolean;
}

/** « Décrire et comparer » — the study's Table 1, with the test the engine chose for each row. */
export function TableAnalysis({
  variables,
  settings,
  onSettings,
  result,
  loading,
  error,
  onExportCsv,
  exporting,
}: Props) {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const set = (patch: Partial<TableAnalysisRequest>) => onSettings({ ...settings, ...patch });
  const [open, setOpen] = useState<Set<string>>(new Set());
  const rowElements = useRef(new Map<string, HTMLTableRowElement>());

  const toggle = useCallback(
    (key: string) =>
      setOpen((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );

  /** From a headline to its row: opened, then brought into view once it has rendered open. */
  const reveal = useCallback((key: string) => {
    setOpen((prev) => new Set(prev).add(key));
    window.requestAnimationFrame(() =>
      rowElements.current.get(key)?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
  }, []);

  const registerRow = useCallback((key: string, element: HTMLTableRowElement | null) => {
    if (element) rowElements.current.set(key, element);
    else rowElements.current.delete(key);
  }, []);

  const absentCandidates = useMemo(
    () =>
      variables.filter(
        (v) =>
          COUNTABLE.has(v.type) &&
          (settings.variables.length === 0 || settings.variables.includes(v.key)),
      ),
    [variables, settings.variables],
  );

  return (
    <div className="space-y-5">
      <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <span className="block text-xs font-medium text-slate-500">
              <Trans>Variables</Trans>
            </span>
            <VariablePicker
              mode="multi"
              variables={variables}
              value={settings.variables}
              onChange={(keys) => set({ variables: keys })}
              placeholder={t`Toutes les variables documentées`}
              className="w-64"
            />
          </div>
          <div className="space-y-1">
            <span className="block text-xs font-medium text-slate-500">
              <Trans>Choix du test</Trans>
            </span>
            <Select value={settings.mode} onValueChange={(mode) => set({ mode: mode as TestMode })}>
              <SelectTrigger className="w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">{t`Automatique (selon les données)`}</SelectItem>
                <SelectItem value="parametric">{t`Paramétrique imposé`}</SelectItem>
                <SelectItem value="nonparametric">{t`Non paramétrique imposé`}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <span className="block text-xs font-medium text-slate-500">
              <Trans>Correction multi-tests</Trans>
            </span>
            <Select value={settings.mcc} onValueChange={(mcc) => set({ mcc: mcc as CorrectionMethod })}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t`Aucune`}</SelectItem>
                <SelectItem value="bh">{t`Benjamini-Hochberg`}</SelectItem>
                <SelectItem value="holm">{t`Holm`}</SelectItem>
                <SelectItem value="bonferroni">{t`Bonferroni`}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <span className="block text-xs font-medium text-slate-500">α</span>
            <Select value={String(settings.alpha)} onValueChange={(alpha) => set({ alpha: Number(alpha) })}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0.05, 0.01, 0.1].map((alpha) => (
                  <SelectItem key={alpha} value={String(alpha)}>
                    {formatNumber(alpha, locale, 2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-1.5 hover:border-primary/50">
                <SlidersHorizontal className="h-4 w-4" />
                <Trans>Absence de mention</Trans>
                {settings.absent.length > 0 && (
                  <span className="rounded-full bg-primary/10 px-1.5 text-2xs font-semibold text-primary">
                    {settings.absent.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96" align="start">
              <p className="mb-3 text-xs text-slate-600">
                <Trans>
                  Certaines variables ne sont écrites que lorsqu'il se passe quelque chose — une
                  complication, une transfusion. Cochées, un dossier qui n'en dit rien compte
                  comme « non » (sur les dossiers opérés lorsqu'elle décrit l'acte) ; sinon il est
                  exclu de la ligne.
                </Trans>
              </p>
              <div className="max-h-72 space-y-1.5 overflow-y-auto">
                {absentCandidates.map((v) => (
                  <label key={v.key} className="flex items-center gap-2 text-sm text-slate-700">
                    <Checkbox
                      checked={settings.absent.includes(v.key)}
                      onCheckedChange={(checked) =>
                        set({
                          absent: checked
                            ? [...settings.absent, v.key]
                            : settings.absent.filter((k) => k !== v.key),
                        })
                      }
                    />
                    <span className="truncate">{v.label}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={onExportCsv}
              disabled={!result || exporting}
              className="gap-1.5 hover:border-primary/50"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              <Trans>CSV</Trans>
            </Button>
          </div>
        </div>
        <VariableChips
          variables={variables}
          value={settings.variables}
          onRemove={(key) => set({ variables: settings.variables.filter((k) => k !== key) })}
        />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {!result && loading && (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {result && (
        <div className={cn("space-y-4 transition-opacity", loading && "opacity-60")}>
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
            {result.compares ? (
              <span>
                <Trans>
                  {result.significant} variable(s) significative(s) sur {result.tested} testée(s)
                </Trans>
                <span className="ml-1 text-slate-400">
                  (α = {formatNumber(result.alpha, locale, 2)}
                  {result.mcc !== "none" && <>, {t`p ajusté`}</>})
                </span>
              </span>
            ) : (
              <span>
                <Trans>Un seul groupe : la population est décrite, sans test.</Trans>
              </span>
            )}
          </div>
          <WarningsList warnings={result.warnings} />
          {result.compares && <Headlines result={result} onReveal={reveal} />}
          <StudyTable result={result} open={open} onToggle={toggle} registerRow={registerRow} />
          <MethodsBox text={result.methods} />
        </div>
      )}
    </div>
  );
}

/**
 * « Ce qui distingue les groupes » — the significant rows, smallest p first, each in the sentence
 * its test wrote. The table below answers « what is each group like »; this answers the question
 * a comparison is opened for, before the reader has to scan forty rows for a bold p.
 */
function Headlines({ result, onReveal }: { result: TableResult; onReveal: (key: string) => void }) {
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const [all, setAll] = useState(false);
  const adjusted = result.mcc !== "none";
  const significant = useMemo(() => {
    const p = (row: TableRow) => (adjusted ? row.test.p_adjusted : row.test.p) ?? 1;
    // Array.prototype.sort is stable: equal p-values keep the table's order.
    return result.rows.filter((row) => row.test.significant).sort((a, b) => p(a) - p(b));
  }, [result.rows, adjusted]);

  if (significant.length === 0) return null;
  const shown = all ? significant : significant.slice(0, HEADLINES);

  return (
    <section className="rounded-xl border border-primary/20 bg-primary/5 p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">
        <Trans>Ce qui distingue les groupes</Trans>
      </h3>
      <ul className="space-y-2">
        {shown.map((row) => {
          const estimate = row.test.estimates[0];
          return (
            <li key={row.key}>
              <button
                type="button"
                onClick={() => onReveal(row.key)}
                className="w-full rounded-lg px-2 py-1.5 text-left text-sm hover:bg-white"
              >
                <span className="font-semibold text-slate-900">{row.label}</span>
                <span className="text-slate-400"> — </span>
                <span className="text-slate-700">{row.test.interpretation}</span>
                {estimate && estimate.value !== null && (
                  <span className="block text-xs text-slate-500">
                    {estimate.name} {formatNumber(estimate.value, locale, 2)}{" "}
                    {formatInterval(estimate.low, estimate.high, locale)}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {significant.length > HEADLINES && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setAll((v) => !v)}
          className="mt-2 text-primary hover:bg-white hover:text-primary"
        >
          {all ? (
            <Trans>Afficher moins</Trans>
          ) : (
            <Trans>Afficher les {significant.length - HEADLINES} autres</Trans>
          )}
        </Button>
      )}
    </section>
  );
}

function StudyTable({
  result,
  open,
  onToggle,
  registerRow,
}: {
  result: TableResult;
  open: Set<string>;
  onToggle: (key: string) => void;
  registerRow: (key: string, element: HTMLTableRowElement | null) => void;
}) {
  const { i18n, t } = useLingui();
  const locale = i18n.locale;
  const adjusted = result.mcc !== "none";
  const columns = result.groups.length + 1 + (result.compares ? 2 : 0);

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="sticky top-0 z-10 bg-slate-50 text-xs text-slate-500">
          <tr className="border-b border-slate-200">
            <th className="px-4 py-3 text-left font-medium">
              <Trans>Variable</Trans>
            </th>
            {result.groups.map((group, index) => (
              <th key={group.id} className="px-3 py-3 text-left font-medium">
                <div className="flex items-center gap-1.5 text-slate-800">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: groupColor(index) }} />
                  <span className="truncate" title={group.label}>{group.label}</span>
                </div>
                <div className="mt-0.5 font-normal text-slate-400">
                  <Trans>
                    {group.patients ?? "—"} patients · {group.episodes ?? "—"} épisodes
                  </Trans>
                </div>
              </th>
            ))}
            {result.compares && (
              <>
                <th className="whitespace-nowrap px-3 py-3 text-right font-medium">{adjusted ? t`p ajusté` : "p"}</th>
                <th className="min-w-[9rem] px-3 py-3 text-left font-medium">
                  <Trans>Test</Trans>
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {result.rows.map((row) => (
            <RowBlock
              key={row.key}
              row={row}
              result={result}
              locale={locale}
              expanded={open.has(row.key)}
              onToggle={() => onToggle(row.key)}
              register={(element) => registerRow(row.key, element)}
              columns={columns}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RowBlock({
  row,
  result,
  locale,
  expanded,
  onToggle,
  register,
  columns,
}: {
  row: TableRow;
  result: TableResult;
  locale: string;
  expanded: boolean;
  onToggle: () => void;
  register: (element: HTMLTableRowElement | null) => void;
  columns: number;
}) {
  const { t } = useLingui();
  const test = row.test;
  const p = result.mcc !== "none" ? test.p_adjusted : test.p;
  const multi = row.type === "multi";
  const tested = result.compares && test.p !== null;
  const readsMedian = tested && RANK_TESTS.has(test.code);
  const summaryClass = (read: boolean) =>
    !tested ? "text-slate-800" : read ? "font-semibold text-slate-900" : "text-slate-500";

  return (
    <Fragment>
      <tr
        ref={register}
        className="cursor-pointer scroll-mt-16 border-t border-slate-200 bg-white hover:bg-primary/5"
        onClick={onToggle}
      >
        <td className="px-4 py-2.5 align-top">
          <div className="flex items-start gap-1.5">
            {expanded ? (
              <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            ) : (
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            )}
            <div className="min-w-0">
              <div className="font-semibold text-slate-900">{row.label}</div>
              <div className="text-2xs text-slate-400">
                {row.unit === "episode" ? t`compté par épisode` : t`compté par patient`}
                {row.absent && <> · {t`absence de mention = non`}</>}
              </div>
            </div>
          </div>
        </td>
        {row.groups.map((cell, index) => (
          <td key={index} className="px-3 py-2.5 align-top text-xs text-slate-500 tabular-nums">
            <Trans>
              {cell.n ?? "—"} / {cell.of ?? "—"} documentés
            </Trans>
          </td>
        ))}
        {result.compares && (
          <>
            <td
              className={cn(
                "whitespace-nowrap px-3 py-2.5 text-right align-top tabular-nums",
                test.significant ? "font-semibold text-primary" : "text-slate-600",
              )}
            >
              {formatP(p, locale)}
            </td>
            <td className="px-3 py-2.5 align-top text-xs text-slate-600">
              {test.p !== null ? (
                <TestName code={test.code} name={test.name} reason={test.reason} reasonText={test.reason_text} />
              ) : (
                "—"
              )}
            </td>
          </>
        )}
      </tr>

      {row.type === "numeric" && (
        <>
          <tr className="text-xs">
            <td className="py-1 pl-10 pr-4 text-slate-500">
              <Trans>Moyenne ± écart-type</Trans>
            </td>
            {row.groups.map((cell, index) => (
              <td key={index} className={cn("px-3 py-1 tabular-nums", summaryClass(!readsMedian))}>
                {cell.summary
                  ? `${formatMeasure(cell.summary.mean, locale)} ± ${formatMeasure(cell.summary.sd, locale)}`
                  : "—"}
              </td>
            ))}
            {result.compares && <td colSpan={2} />}
          </tr>
          <tr className="text-xs">
            <td className="py-1 pl-10 pr-4 text-slate-500">
              <Trans>Médiane [Q1 – Q3]</Trans>
            </td>
            {row.groups.map((cell, index) => (
              <td key={index} className={cn("px-3 py-1 tabular-nums", summaryClass(readsMedian))}>
                {cell.summary
                  ? `${formatMeasure(cell.summary.median, locale)} [${formatMeasure(cell.summary.q1, locale)} – ${formatMeasure(cell.summary.q3, locale)}]`
                  : "—"}
              </td>
            ))}
            {result.compares && <td colSpan={2} />}
          </tr>
        </>
      )}

      {row.type !== "numeric" &&
        row.levels.map((level) => (
          <tr key={level.value} className="text-xs">
            <td className={cn("py-1 pl-10 pr-4", level.synthetic ? "italic text-slate-500" : "text-slate-600")}>
              {level.label}
            </td>
            {level.counts.map((count, index) => (
              <td key={index} className="px-3 py-1 tabular-nums text-slate-800">
                {count === null ? (
                  <span className="text-slate-400" title={t`Masqué : moins de personnes que le seuil d'anonymat`}>
                    —
                  </span>
                ) : (
                  <>
                    {count}{" "}
                    {row.groups[index].n !== null && (
                      <span className="text-slate-400">({formatPercent(count, row.groups[index].n, locale)})</span>
                    )}
                  </>
                )}
              </td>
            ))}
            {result.compares && (
              <>
                <td
                  className={cn(
                    "whitespace-nowrap px-3 py-1 text-right tabular-nums",
                    multi && level.test && (level.test.p_adjusted ?? 1) < result.alpha
                      ? "font-semibold text-primary"
                      : "text-slate-400",
                  )}
                >
                  {multi && level.test ? formatP(level.test.p_adjusted, locale) : ""}
                </td>
                <td />
              </>
            )}
          </tr>
        ))}

      {row.folded && row.folded_label && (
        <tr className="text-xs">
          <td className="py-1 pl-10 pr-4 italic text-slate-400">{row.folded_label}</td>
          {row.folded.map((count, index) => (
            <td key={index} className="px-3 py-1 tabular-nums text-slate-400">
              {count > 0 ? count : ""}
            </td>
          ))}
          {result.compares && <td colSpan={2} />}
        </tr>
      )}

      {expanded && (
        <tr className="bg-slate-50/70">
          <td colSpan={columns} className="px-10 py-3">
            <TestDetail row={row} locale={locale} compares={result.compares} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function TestDetail({ row, locale, compares }: { row: TableRow; locale: string; compares: boolean }) {
  const test = row.test;
  return (
    <div className="space-y-2 text-xs text-slate-600">
      {compares && test.interpretation && <p className="text-sm text-slate-800">{test.interpretation}</p>}
      {compares && test.reason_text && (
        <p>
          <span className="font-medium text-slate-700">
            <Trans>Pourquoi ce test :</Trans>
          </span>{" "}
          {test.reason_text}
        </p>
      )}
      {test.estimates.length > 0 && (
        <ul className="space-y-0.5">
          {test.estimates.map((estimate) => (
            <li key={estimate.code}>
              <span className="font-medium text-slate-700">{estimate.name}</span>{" "}
              {formatNumber(estimate.value, locale, 2)} {formatInterval(estimate.low, estimate.high, locale)}
              {estimate.low !== null && (
                <span className="text-slate-400">
                  {" "}
                  <Trans>(IC 95 %)</Trans>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {test.effect && test.effect.value !== null && (
        <p>
          <span className="font-medium text-slate-700">{test.effect.name}</span> ={" "}
          {formatNumber(test.effect.value, locale, 2)}
        </p>
      )}
      {test.statistic !== null && (
        <p className="text-slate-400">
          <Trans>Statistique</Trans> {formatNumber(test.statistic, locale, 3)}
          {test.df !== null && (
            <>
              {" "}
              · <Trans>ddl</Trans> {formatNumber(test.df, locale, test.df % 1 === 0 ? 0 : 1)}
            </>
          )}
          {" "}· p {formatP(test.p, locale)}
        </p>
      )}
      <WarningsList warnings={row.warnings} />
    </div>
  );
}
